import React, { lazy, Suspense } from "react";
import { connect } from 'react-redux';
import {
    updateInstanceData,
    updateBodyServer,
} from "../../../../../../redux/actions/DeploymentActions";
import { createFetch } from "../../../../../../helpers/fetch/FetchRequest";
import { DEBUG } from "../../../../../../settings/global";
import { removeLastModalLayer } from "../../../../../../redux/actions/ModalActions";
import Loading from "../../../../../../components/Loading";
import styles from './Image.module.scss'
import { defineIfArchitectureIsStrict } from "./helpers";
import showModalDefault from "../../../../../../helpers/ui/ModalDefault";
import { dispatchNotification } from "../../../../../../layouts/app/components/notificationSystem/notifications";
import InstanceLayout from "../../layouts/InstanceLayout";
import { API_APP_INTERNAL } from "../../../../../../settings/endpoints/app";

const ImageSelect = lazy(() => import('./components/ImageSelect'));

class Image extends React.Component {

    constructor(props) {
        super(props);

        this.state = {
            error: null,
        }
    }

    checkArchitecture = (image, server) => {

        const {currentCloud} = this.props;

        if (defineIfArchitectureIsStrict(currentCloud.code)) {
            return server?.architecture === image?.architecture;
        } else {
            return true
        }
    }

    componentDidUpdate(prevProps, prevState, snapshot) {

        let {instanceData, bodyServer, currentCloud, dispatch} = this.props;
        let {regionsStatus, serversStatus, imagesStatus, images} = instanceData;
        let {
            regionsFetch,
            imagesRegionsDependency, imagesServersDependency, imagesFetch, imagesURL
        } = currentCloud.config ? currentCloud.config : {};

        if (
            !!imagesFetch &&
            (
                imagesRegionsDependency && !!regionsFetch && bodyServer.region
                && (
                    regionsStatus !== prevProps.instanceData.regionsStatus && regionsStatus === 'success'
                    || bodyServer.region !== prevProps.bodyServer.region
                )
                ||
                imagesServersDependency && !!imagesFetch && bodyServer.server
                && (
                    serversStatus !== prevProps.instanceData.serversStatus && serversStatus === 'success'
                    || bodyServer.server !== prevProps.bodyServer.server
                )
            )
        ) {

            let urlAddition = '';

            if (!!imagesRegionsDependency && !imagesServersDependency) urlAddition = `?region=${bodyServer.region}`;
            if (!!imagesRegionsDependency && !!imagesServersDependency) urlAddition = `?region=${bodyServer.region}&server=${bodyServer.server}`;

            dispatch(updateInstanceData({imagesStatus: 'loading'}));
            createFetch({
                URL: '/api/instance',
                requestTitle: 'Images',
                method: 'POST',
                body: {
                    url: !!imagesURL ? imagesURL + urlAddition : `${API_APP_INTERNAL}/${currentCloud['code']}/images` + urlAddition,
                },
                onSuccess: response => {
                    dispatch(updateInstanceData({imagesStatus: 'success', images: response['images']}));
                },
                onError: () => dispatch(updateInstanceData({imagesStatus: 'error'})),
            })
        }

        if (
            !!imagesFetch
            && (
                imagesStatus !== prevProps.instanceData.imagesStatus
                || serversStatus !== prevProps.instanceData.serversStatus
                || bodyServer.server !== prevProps.bodyServer.server
            )
            && imagesStatus === 'success'
            && serversStatus === 'success'
            && !!bodyServer.server
        ) {

            const server = instanceData.servers.find(s => s.id === bodyServer.server);
            let selectedImageData = null;
            const firstAvailable = images.find(i => (
                (!('status' in i) || i.status === 'available') && this.checkArchitecture(i, server)
            ));

            if (!firstAvailable && DEBUG) {
                dispatchNotification({
                    message: `No image with matching architecture or with "available" status or absent "status" key were found.`,
                    type: 'error',
                })
            }

            if (!!bodyServer.os) {
                selectedImageData = images.find(i => (
                    i.id === bodyServer.os && this.checkArchitecture(i, server)
                ));
                if (
                    // check for valid redeployment data
                    !selectedImageData || (
                        'status' in selectedImageData && selectedImageData?.status !== 'available'
                    )
                    // check if both server and image support the same specific architecture
                    || !!server.architecture && server.architecture !== selectedImageData.architecture
                ) {selectedImageData = firstAvailable}
            } else {
                selectedImageData = firstAvailable;
            }

            if (!selectedImageData) {
                dispatch(updateInstanceData({imagesStatus: 'error'}));
                return;
            }

            dispatch(updateBodyServer({os: selectedImageData.id + ''}));
        }
    }

    render() {

        const {instanceData, bodyServer, currentCloud} = this.props;
        const {regionsStatus, serversStatus, images, imagesStatus} = instanceData;
        const server = instanceData.servers?.find(s => s.id === bodyServer.server);
        const selectedImage = !!images && !!bodyServer.os && images.find(i => (
            i.id === bodyServer.os && this.checkArchitecture(i, server)
        ));

        if (
            !currentCloud.config && !this.state.error
            || (
                !!currentCloud.config['regionsFetch'] && (regionsStatus !== 'success' || !bodyServer['region'])
                || !!currentCloud.config['serversFetch'] && (serversStatus !== 'success' || !bodyServer['server'])
                || !!currentCloud.config['imagesFetch'] && (imagesStatus !== 'success' || !selectedImage)
            )
        ) return (
            <div className={styles.image} id='image-anchor'>
                <Loading type='component-cover'/>
            </div>
        );

        let {description, name, id} = selectedImage;

        let disabled = false;  // @todo after images are done
        let imageName = description || name || id;

        let icon = 'linux';
        const icons = ['centos', 'fedora', 'redhat', 'suse', 'ubuntu', 'windows'];
        let foundIcon = icons.find(i => imageName.toLowerCase().includes(i.toLowerCase()));

        if (!!foundIcon) icon = foundIcon;

        return (
            <InstanceLayout
                title='Image'
                id='image-anchor'
                disabled={disabled}
                onClick={() => {
                    showModalDefault({
                        component: (
                            <Suspense fallback={<Loading/>}>
                                <ImageSelect
                                    images={images}
                                    selectImage={image => {
                                        this.props.dispatch(updateBodyServer({os: image}));
                                        this.props.dispatch(removeLastModalLayer());
                                    }}
                                    currentCloud={this.props.currentCloud}
                                    server={this.props.instanceData.servers.find(s => s.id === this.props.bodyServer.server)}
                                />
                            </Suspense>
                        ),
                        title: 'Select image',
                        maxWidth: '600px',
                    })
                }}
            >
                <div className={styles.content}>
                    <i className={`fab fa-${icon} ${styles.content__icon}`}/>
                    <p className={styles.content__name}>
                        {selectedImage.description || selectedImage.name || selectedImage.id}
                        {selectedImage.architecture ? ' (' + selectedImage.architecture + ')' : ''}
                    </p>
                </div>
            </InstanceLayout>
        )
    }
}

const mapStateToProps = state => ({
    currentCloud: state.deployment.currentCloud,
    instanceData: state.deployment.instanceData,
    requirements: state.deployment.requirements,
    bodyServer: state.deployment.bodyServer,
});

export default connect(mapStateToProps)(Image)